import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { api } from '../../../core/network/api';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { useKpis } from '../contexts/kpis';
import { buildReproductiveReport } from '../utils/reproductive-report';
import ProductionCards from './production';
import FunnelChart from './funnel';
import RatesGrid from './rates';
import OocytesChart from './oocytes-chart';
import FrequencyChart from './frequency-chart';

import './styles/dashboard.css';

// Panel de estadísticas reutilizable con ALCANCE FIJO (scope: { farm_id } | { client_id }).
// Mismas secciones que el dashboard (admin-kpis.md, view=full) pero sin el selector
// cliente/finca — el alcance lo fija el item de la sección; solo deja el rango de fechas.
const todayStr = () => new Date().toLocaleDateString('en-CA');
const yearStart = () => `${new Date().getFullYear()}-01-01`;

export default function KpiStats({ scope, title, subtitle, backTo, backLabel }) {
    const [range, setRange] = useState({ from: yearStart(), to: todayStr() });
    const { data, isLoading, isFetching, error, refetch } = useKpis('full', { ...scope, ...range });

    // Informe reproductivo "por finca" (admin-stats.md §"Stats combinadas", view=report):
    // se pide bajo demanda el bloque `report` del endpoint por cliente/finca (mono-anclado
    // en T.E., respeta el mismo rango de fechas de arriba) y se arma el PDF.
    const qc = useQueryClient();
    const statsPath = scope?.client_id
        ? `admin/clients/${scope.client_id}/stats`
        : scope?.farm_id
            ? `admin/farms/${scope.farm_id}/stats`
            : null;

    const { mutateAsync: generatePdf } = useGeneratePdf();
    const [pdfBusy, setPdfBusy] = useState(false);
    const onPdf = async () => {
        if (!statsPath || pdfBusy) return;
        setPdfBusy(true);
        try {
            const params = { view: 'report' };
            if (range.from) params.from = range.from;
            if (range.to) params.to = range.to;
            const reportData = await qc.fetchQuery({
                queryKey: ['combined-stats', statsPath, 'report', range.from || null, range.to || null],
                queryFn: ({ signal }) => api.get(statsPath, { params, signal }).then((r) => r.data),
                staleTime: 60_000,
            });
            const { html, filename, options } = buildReproductiveReport(reportData);
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            toast.success('Informe generado');
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe'); // 403 lo avisa el interceptor
        } finally {
            setPdfBusy(false);
        }
    };

    return (
        <div className={`report-panel ${isFetching ? 'report-panel--busy' : ''}`}>
            <header className="report-panel__header">
                {backTo && <Link to={backTo} className="report-panel__back">{backLabel || 'Volver'}</Link>}
                {title && <h1 className="report-panel__title">{title}</h1>}
                {subtitle && <p className="report-panel__subtitle">{subtitle}</p>}
            </header>

            <div className="report-filters">
                <label className="report-filters__group">
                    <span className="report-filters__label">Desde</span>
                    <input type="date" value={range.from} max={range.to || todayStr()} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} disabled={isFetching} />
                </label>
                <label className="report-filters__group">
                    <span className="report-filters__label">Hasta</span>
                    <input type="date" value={range.to} min={range.from || undefined} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} disabled={isFetching} />
                </label>
                {/* Rango abierto: sin 'desde' → todo ≤ 'hasta'; sin 'hasta' → todo ≥ 'desde'; ambos vacíos → período total. */}
                <button type="button" className="report-filters__clear" onClick={() => setRange({ from: '', to: '' })} disabled={isFetching || (!range.from && !range.to)} title="Sin límites de fecha (todo el historial)">
                    Período total
                </button>
                <button type="button" className="report-filters__refresh" onClick={() => refetch()} disabled={isFetching}>
                    {isFetching ? 'Actualizando…' : 'Actualizar'}
                </button>
                <Gate domain="reports" action="generate">
                    <button type="button" className="report-filters__pdf" onClick={onPdf} disabled={pdfBusy || !statsPath}>
                        {pdfBusy ? 'Generando…' : 'Informe reproductivo'}
                    </button>
                </Gate>
            </div>

            {error && (
                <div className="report-error">
                    {error.status === 403 ? 'No tenés permiso para ver los informes.' : (error.message || 'No se pudieron cargar los informes.')}
                </div>
            )}

            {isLoading && !data ? (
                <div className="report-state">Cargando estadísticas…</div>
            ) : (
                <>
                    <div className="report-grid">
                        <div className="report-grid__main">
                            <section className="report-section">
                                <h2 className="report-section__title">Producción</h2>
                                <ProductionCards production={data?.production} />
                            </section>
                            <RatesGrid rates={data?.rates} />
                        </div>
                        <div className="report-grid__side">
                            <FunnelChart funnel={data?.funnel} />
                        </div>
                    </div>

                    <section className="report-trends">
                        <OocytesChart data={data?.trends?.oocytes_per_session} />
                        <FrequencyChart series={data?.trends?.aspiration_frequency} label="Frecuencia de aspiraciones (OPU)" tone="accent" />
                        <FrequencyChart series={data?.trends?.transfer_frequency} label="Frecuencia de transferencias (TE)" tone="primary" />
                    </section>
                </>
            )}
        </div>
    );
}
